import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import useAuth from '@/auth/application/useAuth';
import { useGetDatabases } from '@/userManagement/application/adapters/users/useGetDatabases';
import { getUserManagementGateway } from '@/userManagement/application/getUserManagementGateway';
import { operatorScopeDisplayLabel } from '@/userManagement/application/utils/operatorScopeDisplayLabel';

import { OPERATOR_PERMISSIONS_KEYS } from './queryKeys';

const OPERATOR_PERMISSIONS_STALE_MS = 5 * 60 * 1000;

export interface UseOperatorPermissionsResult {
  featureKeys: string[] | undefined;
  toolRoleName: string | null | undefined;
  isReadOnlyOperator: boolean;
  allDatabases: boolean;
  databaseNames: string[];
  scopeLabel: string | undefined;
  isSuccess: boolean;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Loads Plane A operator permissions from the User Management API (TROC-197).
 * Cached for five minutes; refetch on window focus is disabled to avoid UI flicker.
 */
export function useOperatorPermissions(): UseOperatorPermissionsResult {
  const { isLoggedIn } = useAuth();
  const { databases } = useGetDatabases();

  const { data, isSuccess, isLoading, error } = useQuery({
    queryKey: OPERATOR_PERMISSIONS_KEYS.current(),
    queryFn: async () => getUserManagementGateway().getOperatorPermissions(),
    enabled: isLoggedIn,
    staleTime: OPERATOR_PERMISSIONS_STALE_MS,
    refetchOnWindowFocus: false,
    retry: 1
  });

  const databaseNames = data?.databaseNames ?? [];
  const allDatabases = data?.allDatabases ?? true;
  const scopeLabel = useMemo(
    () => operatorScopeDisplayLabel(data?.scopeLabel, data?.databaseNames ?? [], data?.allDatabases ?? true, databases),
    [data?.allDatabases, data?.databaseNames, data?.scopeLabel, databases]
  );

  return {
    featureKeys: data?.featureKeys,
    toolRoleName: data?.toolRoleName,
    isReadOnlyOperator: data?.isReadOnlyOperator ?? false,
    allDatabases,
    databaseNames,
    scopeLabel,
    isSuccess,
    isLoading,
    error: error instanceof Error ? error : null
  };
}
